import { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS, normalizeCarriers, normalizeDataUsage } from '../api/api';

const safe = (p) => p.catch(() => null);

export function useDistrictData(districtName, page = 1, search = '') {
  const [state, setState] = useState({ loading: true, devicesLoading: true, error: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Summary + data usage — only re-fetches when the district changes
  useEffect(() => {
    if (!districtName) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const name = districtName.toUpperCase();

    Promise.all([
      fetch(ENDPOINTS.district(name)).then((r) => r.json()),
      safe(fetch(ENDPOINTS.districtDataUsage(name)).then((r) => r.json())),
    ])
      .then(([summary, dataUsage]) => {
        if (!summary) {
          setState((s) => ({ ...s, loading: false, error: 'Failed to load district data' }));
          return;
        }
        const circuits = Object.entries(summary.devicesPerProvince ?? {})
          .map(([n, count]) => ({ name: n, count }))
          .sort((a, b) => b.count - a.count);

        setState((s) => ({
          ...s,
          loading: false,
          error: null,
          stats: {
            devices:  (summary.totalDevices   ?? 0).toLocaleString(),
            schools:  (summary.uniqueNameCount ?? 0).toLocaleString(),
            circuits: (summary.provinceCount   ?? 0).toLocaleString(),
          },
          circuits,
          carriers:  normalizeCarriers(summary.carrierStats ?? []),
          dataUsage: normalizeDataUsage(Array.isArray(dataUsage) ? dataUsage : []),
        }));
      })
      .catch((err) => setState((s) => ({ ...s, loading: false, error: err.message })));
  }, [districtName, refreshKey]);

  // Paginated devices — re-fetches when page changes without touching loading
  useEffect(() => {
    if (!districtName) return;
    setState((s) => ({ ...s, devicesLoading: true }));
    const name = districtName.toUpperCase();

    safe(fetch(ENDPOINTS.districtDevices(name, page, 10, search)).then((r) => r.json()))
      .then((paginated) => {
        setState((s) => ({
          ...s,
          devicesLoading: false,
          devices:      paginated?.devices      ?? [],
          totalPages:   paginated?.totalPages   ?? 1,
          totalDevices: paginated?.totalDevices ?? 0,
        }));
      });
  }, [districtName, page, search, refreshKey]);

  return { ...state, refresh };
}
